import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TourService } from '@services/tour.service';
import {
  EnvironmentService,
  SystemType,
  SystemTypes,
} from '@services/environment.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface NavMenuChildItem {
  link: string;
  title: string;
  translation_key?: string;
  icon?: string;
  hidden?: boolean;
  tourId?: string;
  stepId?: string;
  position?: string;
}

export interface NavMenuItem {
  link: string;
  title: string;
  translation_key?: string;
  icon: string;
  tourId?: string;
  stepId?: string;
  position?: string;
  badge?: () => any;
  children?: NavMenuChildItem[];
  expanded?: boolean;
  loadingChildren?: boolean;
}

export interface FooterMenuItem {
  type: 'link' | 'modal' | 'url';
  link?: string;
  url?: string;
  modal?: string;
  title: string;
  translation_key?: string;
  icon: string;
}

export interface FeatureOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-sidebar-nav',
  templateUrl: './sidebar-nav.component.html',
  styleUrls: ['./sidebar-nav.component.scss'],
  standalone: false,
})
export class SidebarNavComponent implements OnInit, OnChanges {
  @Input() menuItems: NavMenuItem[] = [];
  @Input() footerItems: FooterMenuItem[] = [];
  @Input() isLoading = true;
  @Input() selectedType: string;
  @Input() features: FeatureOption[] = [];
  @Input() activeFeature: string;
  @Input() isCollapsed = false;

  @Output() footerModalOpen = new EventEmitter<string>();
  @Output() instanceChanged = new EventEmitter<{
    type: string;
    force: boolean;
  }>();
  @Output() activeFeatureChanged = new EventEmitter<any>();
  systemTypeOptions = SystemTypes.map(type => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    value: type,
  }));
  selectedSystemType: SystemType;
  allowSystemSwitch = false;
  destroy$ = new Subject();
  expandedItems: Set<string> = new Set();
  hoveredParent: NavMenuItem | null = null;
  flyoutTop = 0;
  private flyoutLeaveTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public router: Router,
    public tourService: TourService,
    private environmentService: EnvironmentService
  ) {}

  ngOnInit(): void {
    this.environmentService
      .getInstanceDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe(details => {
        this.allowSystemSwitch =
          this.environmentService.isSystemSwitchAllowed(details);
        if (details?.system_type) {
          this.selectedSystemType = details.system_type;
        }
      });

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.expandActiveParents();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuItems'] && this.menuItems?.length) {
      this.expandActiveParents();
    }
    if (
      changes['isCollapsed'] &&
      changes['isCollapsed'].currentValue === true
    ) {
      this.expandActiveParents();
    }
  }

  ngOnDestroy() {
    this.clearFlyoutLeaveTimeout();
    this.destroy$.next('');
    this.destroy$.complete();
  }

  isCoursesItem(button: NavMenuItem): boolean {
    return button.link === '/courses';
  }


  showFlyoutForParent(button: NavMenuItem): boolean {
    return !!(
      this.isCollapsed &&
      this.hasVisibleChildren(button) &&
      !this.isChildActive(button) &&
      !this.isParentRouteActive(button)
    );
  }

  onParentMouseEnter(button: NavMenuItem, event: MouseEvent): void {
    if (!this.showFlyoutForParent(button)) return;
    this.clearFlyoutLeaveTimeout();
    this.hoveredParent = button;
    const el = (event.target as HTMLElement).closest(
      '.nav-item-parent'
    ) as HTMLElement | null;
    this.flyoutTop = el ? el.getBoundingClientRect().top : 0;
  }

  onParentMouseLeave(): void {
    this.scheduleFlyoutLeave();
  }

  onFlyoutMouseEnter(): void {
    this.clearFlyoutLeaveTimeout();
  }

  onFlyoutMouseLeave(): void {
    this.scheduleFlyoutLeave();
  }

  private scheduleFlyoutLeave(): void {
    this.clearFlyoutLeaveTimeout();
    this.flyoutLeaveTimeout = setTimeout(() => {
      this.hoveredParent = null;
      this.flyoutLeaveTimeout = null;
    }, 150);
  }

  private clearFlyoutLeaveTimeout(): void {
    if (this.flyoutLeaveTimeout != null) {
      clearTimeout(this.flyoutLeaveTimeout);
      this.flyoutLeaveTimeout = null;
    }
  }

  private expandActiveParents(): void {
    if (!this.menuItems?.length) return;

    this.expandedItems.clear();
    this.menuItems.forEach(item => {
      const key = item.link || item.title;
      const shouldExpand =
        (this.hasVisibleChildren(item) && this.isChildActive(item)) ||
        (this.isCoursesItem(item) && this.isOnCourseChildRoute());
      if (shouldExpand) {
        this.expandedItems.add(key);
      }
    });
  }

  private isOnCourseChildRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return /^\/courses\/\d+(\/|$)/.test(path);
  }

  onNavItemClick(button: NavMenuItem, event: Event): void {
    this.scheduleFlyoutLeave();
    if (this.hasVisibleChildren(button)) {
      event.preventDefault();
      event.stopPropagation();
      const key = button.link || button.title;
      const wasExpanded = this.expandedItems.has(key);
      if (wasExpanded) {
        this.expandedItems.delete(key);
      } else {
        this.expandedItems.add(key);
        if (button.tourId && button.stepId) {
          setTimeout(() => {
            this.tourService.nextStep(button.tourId, button.stepId);
          }, 400);
        }
      }
      return;
    }
    if (button.tourId && button.stepId) {
      this.tourService.nextStep(button.tourId, button.stepId);
    }
  }

  toggleExpand(button: NavMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const key = button.link || button.title;
    if (this.expandedItems.has(key)) {
      this.expandedItems.delete(key);
    } else {
      this.expandedItems.add(key);
    }
  }

  isExpanded(button: NavMenuItem): boolean {
    const key = button.link || button.title;
    return this.expandedItems.has(key);
  }

  hasVisibleChildren(button: NavMenuItem): boolean {
    return button.children?.some(child => !child.hidden) ?? false;
  }

  getVisibleChildren(button: NavMenuItem): NavMenuChildItem[] {
    return button.children?.filter(child => !child.hidden) ?? [];
  }

  isChildActive(button: NavMenuItem): boolean {
    if (!button.children) return false;
    const visibleChildren = this.getVisibleChildren(button);
    return visibleChildren.some(child =>
      this.router.url.startsWith(child.link)
    );
  }

  isParentRouteActive(button: NavMenuItem): boolean {
    return this.router.url.startsWith(button.link);
  }

  onFooterModalClick(modal: string): void {
    this.footerModalOpen.emit(modal);
  }

  getTabIndex(button: NavMenuItem): number {
    return button.link && this.router.isActive(button.link, false) ? -1 : 0;
  }

  onInstanceChange(event: { type: string; force: boolean }): void {
    this.instanceChanged.emit(event);
  }

  onFeatureChange(event: any): void {
    this.activeFeatureChanged.emit(event);
  }

  onSystemTypeChange(event: { value: SystemType }): void {
    this.environmentService.setSystemTypeOverride(event.value);
  }
}
